import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, TvIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateClientData) => void;
  platformId: string;
  isLoading?: boolean;
  initialData?: any;
}

export interface CreateClientData {
  name: string;
  clientType: 'web_app' | 'mobile_web' | 'mobile_app' | 'tv_app';
  url?: string;
  domain?: string;
  bundleName?: string;
  operatingSystem?: string;
  appVersion?: string;
  clientConfig?: Record<string, any>;
  metadata?: Record<string, any>;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  platformId: _platformId,
  isLoading = false,
  initialData,
}) => {
  const [formData, setFormData] = useState<CreateClientData>({
    name: initialData?.name || '',
    clientType: initialData?.clientType || 'web_app',
    url: initialData?.url || '',
    domain: initialData?.domain || '',
    bundleName: initialData?.bundleName || '',
    operatingSystem: initialData?.operatingSystem || '',
    appVersion: initialData?.appVersion || '1.0.0',
    clientConfig: initialData?.clientConfig || {},
    metadata: initialData?.metadata || {},
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clientTypes = [
    {
      value: 'web_app',
      label: 'Web Uygulaması',
      icon: <ComputerDesktopIcon className="h-5 w-5" />,
      description: 'Masaüstü ve web tarayıcıları için'
    },
    {
      value: 'mobile_web',
      label: 'Mobil Web',
      icon: <GlobeAltIcon className="h-5 w-5" />,
      description: 'Mobil tarayıcılar için optimize edilmiş'
    },
    {
      value: 'mobile_app',
      label: 'Mobil Uygulama',
      icon: <DevicePhoneMobileIcon className="h-5 w-5" />,
      description: 'iOS ve Android uygulamaları'
    },
    {
      value: 'tv_app',
      label: 'TV Uygulaması',
      icon: <TvIcon className="h-5 w-5" />,
      description: 'Smart TV ve set-top box uygulamaları'
    },
  ];

  const operatingSystems = {
    mobile_app: ['ios', 'android'],
    tv_app: ['tvos', 'android_tv', 'tizen', 'webos', 'roku']
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'İstemci adı gereklidir';
    } else if (formData.name.length < 2) {
      newErrors.name = 'İstemci adı en az 2 karakter olmalıdır';
    }

    // Type specific validations
    if (formData.clientType === 'web_app' || formData.clientType === 'mobile_web') {
      if (!formData.url?.trim()) {
        newErrors.url = 'URL gereklidir';
      } else if (!isValidUrl(formData.url)) {
        newErrors.url = 'Geçerli bir URL giriniz';
      }
      
      if (formData.domain && !isValidDomain(formData.domain)) {
        newErrors.domain = 'Geçerli bir domain giriniz';
      }
    }

    if (formData.clientType === 'mobile_app' || formData.clientType === 'tv_app') {
      if (!formData.bundleName?.trim()) {
        newErrors.bundleName = 'Bundle/Package adı gereklidir';
      }
      
      if (!formData.operatingSystem) {
        newErrors.operatingSystem = 'İşletim sistemi seçiniz';
      }
      
      if (!formData.appVersion?.trim()) {
        newErrors.appVersion = 'Uygulama versiyonu gereklidir';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isValidDomain = (domain: string): boolean => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-._]*[a-zA-Z0-9]$/;
    return domainRegex.test(domain);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Clean up data based on client type
    const cleanedData = { ...formData };
    
    if (formData.clientType === 'web_app' || formData.clientType === 'mobile_web') {
      delete cleanedData.bundleName;
      delete cleanedData.operatingSystem;
      delete cleanedData.appVersion;
    } else {
      delete cleanedData.url;
      delete cleanedData.domain;
    }

    onSubmit(cleanedData);
  };

  const handleInputChange = (field: keyof CreateClientData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleClientTypeChange = (clientType: CreateClientData['clientType']) => {
    setFormData(prev => ({
      ...prev,
      clientType,
      // Reset type-specific fields
      url: '',
      domain: '',
      bundleName: '',
      operatingSystem: '',
      appVersion: clientType === 'mobile_app' || clientType === 'tv_app' ? '1.0.0' : '',
    }));
    setErrors({});
  };

  const resetForm = () => {
    setFormData({
      name: '',
      clientType: 'web_app',
      url: '',
      domain: '',
      bundleName: '',
      operatingSystem: '',
      appVersion: '1.0.0',
      clientConfig: {},
      metadata: {},
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-white flex items-center justify-between"
                >
                  <span>{initialData ? 'İstemci Düzenle' : 'Yeni İstemci Oluştur'}</span>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                  {/* Client Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      İstemci Adı *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                      placeholder="İstemci adını girin"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
                    )}
                  </div>

                  {/* Client Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      İstemci Türü *
                    </label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {clientTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleClientTypeChange(type.value as CreateClientData['clientType'])}
                          className={`relative rounded-lg border p-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            formData.clientType === type.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900 dark:border-blue-400'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`${formData.clientType === type.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                              {type.icon}
                            </div>
                            <div className="ml-3">
                              <div className={`text-sm font-medium ${formData.clientType === type.value ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-gray-100'}`}>
                                {type.label}
                              </div>
                              <div className={`text-xs ${formData.clientType === type.value ? 'text-blue-700 dark:text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>
                                {type.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Web App & Mobile Web Fields */}
                  {(formData.clientType === 'web_app' || formData.clientType === 'mobile_web') && (
                    <>
                      <div>
                        <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          URL *
                        </label>
                        <input
                          type="url"
                          id="url"
                          value={formData.url}
                          onChange={(e) => handleInputChange('url', e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                          placeholder="https://example.com"
                        />
                        {errors.url && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.url}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Domain
                        </label>
                        <input
                          type="text"
                          id="domain"
                          value={formData.domain}
                          onChange={(e) => handleInputChange('domain', e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                          placeholder="example.com (isteğe bağlı)"
                        />
                        {errors.domain && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.domain}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Mobile App & TV App Fields */}
                  {(formData.clientType === 'mobile_app' || formData.clientType === 'tv_app') && (
                    <>
                      <div>
                        <label htmlFor="bundleName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {formData.clientType === 'mobile_app' ? 'Bundle ID / Package Name' : 'Package Name'} *
                        </label>
                        <input
                          type="text"
                          id="bundleName"
                          value={formData.bundleName}
                          onChange={(e) => handleInputChange('bundleName', e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                          placeholder={formData.clientType === 'mobile_app' ? 'com.example.app' : 'com.example.tvapp'}
                        />
                        {errors.bundleName && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.bundleName}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="operatingSystem" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          İşletim Sistemi *
                        </label>
                        <select
                          id="operatingSystem"
                          value={formData.operatingSystem}
                          onChange={(e) => handleInputChange('operatingSystem', e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">İşletim sistemi seçin</option>
                          {operatingSystems[formData.clientType as keyof typeof operatingSystems]?.map((os) => (
                            <option key={os} value={os}>
                              {os === 'ios' ? 'iOS' : 
                               os === 'android' ? 'Android' :
                               os === 'tvos' ? 'tvOS (Apple TV)' :
                               os === 'android_tv' ? 'Android TV' :
                               os === 'tizen' ? 'Tizen (Samsung)' :
                               os === 'webos' ? 'webOS (LG)' :
                               os === 'roku' ? 'Roku OS' : os}
                            </option>
                          ))}
                        </select>
                        {errors.operatingSystem && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.operatingSystem}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="appVersion" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Uygulama Versiyonu *
                        </label>
                        <input
                          type="text"
                          id="appVersion"
                          value={formData.appVersion}
                          onChange={(e) => handleInputChange('appVersion', e.target.value)}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                          placeholder="1.0.0"
                        />
                        {errors.appVersion && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.appVersion}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isLoading}
                      className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-800"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-800"
                    >
                      {isLoading ? (initialData ? 'Güncelleniyor...' : 'Oluşturuluyor...') : (initialData ? 'İstemci Güncelle' : 'İstemci Oluştur')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateClientModal; 